'use client';

import { useState, useRef } from 'react';
import { useRouter } from '@/i18n/routing';
import { createService, updateService } from "@/lib/actions/services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, Upload, X } from "lucide-react";
import { Link } from "@/i18n/routing";

import { RichTextEditor } from "../shared/RichTextEditor";
import { MediaImg } from '@/components/shared/MediaImg';
import { parseJsonField } from '@/lib/utils';

interface ServiceFormProps {
  initialData?: any;
  categories: any[];
  allInsights?: any[];
}

export function ServiceForm({ initialData, categories, allInsights = [] }: ServiceFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const homeFileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!initialData;

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    slug: initialData?.slug || '',
    image: initialData?.image || '',
    homeImage: initialData?.homeImage || '',
    categoryId: initialData?.categoryId || '',
    shortDescription: initialData?.shortDescription || '',
    longDescription: initialData?.longDescription || '',
    order: initialData?.order || 0,
    features: parseJsonField(initialData?.features, []),
    outputs: parseJsonField(initialData?.outputs, []),
    steps: parseJsonField(initialData?.steps, []),
    modules: parseJsonField(initialData?.modules, []),
    individualAssessments: parseJsonField(initialData?.individualAssessments, []),
    organizationalAssessments: parseJsonField(initialData?.organizationalAssessments, []),
    insightIds: initialData?.insights ? initialData.insights.map((i: any) => i.id) : [],
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'title' && !isEditing) {
        newData.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
      return newData;
    });
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData((prev) => ({ ...prev, categoryId: value }));
  };

  const handleInsightToggle = (id: string) => {
    setFormData((prev) => {
      const currentIds = [...prev.insightIds];
      const index = currentIds.indexOf(id);
      if (index === -1) {
        currentIds.push(id);
      } else {
        currentIds.splice(index, 1);
      }
      return { ...prev, insightIds: currentIds };
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: 'image' | 'homeImage') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setIsUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('type', 'services');

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: uploadData,
        credentials: 'include',
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({ ...prev, [field]: result.url }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      toast.error('An error occurred during upload');
    } finally {
      setIsUploading(false);
      if (field === 'image' && fileInputRef.current) fileInputRef.current.value = '';
      if (field === 'homeImage' && homeFileInputRef.current) homeFileInputRef.current.value = '';
    }
  };

  const removeImage = (field: 'image' | 'homeImage') => {
    setFormData(prev => ({ ...prev, [field]: '' }));
  };

  const handleArrayChange = (field: string, index: number, value: string) => {
    setFormData((prev: any) => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const handleStepChange = (index: number, key: string, value: string) => {
    setFormData((prev: any) => {
      const newSteps = [...prev.steps];
      newSteps[index] = { ...newSteps[index], [key]: value };
      return { ...prev, steps: newSteps };
    });
  };

  const handleStepManualChange = (index: number, key: string, value: string) => {
    setFormData((prev: any) => {
      const newSteps = [...prev.steps];
      newSteps[index] = { ...newSteps[index], [key]: value };
      return { ...prev, steps: newSteps };
    });
  };

  const addArrayItem = (field: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: [...prev[field], field === 'steps' ? { title: '', description: '' } : '']
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: prev[field].filter((_: any, i: number) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = isEditing 
        ? await updateService(initialData.id, formData)
        : await createService(formData);

      if (result.success) {
        toast.success(`Service ${isEditing ? 'updated' : 'created'} successfully`);
        router.push('/admin/services');
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to save service");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/admin/services">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold text-navy">
          {isEditing ? 'Edit Service' : 'Add New Service'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Service Title</Label>
                  <Input id="title" name="title" value={formData.title} onChange={handleChange} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug</Label>
                  <Input id="slug" name="slug" value={formData.slug} onChange={handleChange} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shortDescription">Short Description</Label>
                  <RichTextEditor 
                    value={formData.shortDescription} 
                    onChange={(val) => handleManualChange('shortDescription', val)} 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longDescription">Full Content (Rich Text)</Label>
                  <RichTextEditor 
                    value={formData.longDescription} 
                    onChange={(val) => handleManualChange('longDescription', val)} 
                  />
                </div>

              </CardContent>
            </Card>

            {/* Steps Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Process Steps (for Alignment)</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('steps')}>
                  <Plus className="h-4 w-4 mr-2" /> Add Step
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.steps.map((step: any, index: number) => (
                  <div key={index} className="flex gap-4 items-start p-4 bg-light/30 rounded-lg border border-light">
                    <div className="flex-1 space-y-4">
                      <Input placeholder="Step Title" value={step.title} onChange={(e) => handleStepChange(index, 'title', e.target.value)} />
                      <RichTextEditor value={step.description} onChange={(val) => handleStepManualChange(index, 'description', val)} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('steps', index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Assessments Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">Individual Assessments</CardTitle>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addArrayItem('individualAssessments')}>
                    <Plus className="h-3 w-3" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-2">
                  {formData.individualAssessments.map((item: string, index: number) => (
                    <div key={index} className="flex gap-2">
                      <Input value={item} onChange={(e) => handleArrayChange('individualAssessments', index, e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('individualAssessments', index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">Organizational Assessments</CardTitle>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addArrayItem('organizationalAssessments')}>
                    <Plus className="h-3 w-3" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-2">
                  {formData.organizationalAssessments.map((item: string, index: number) => (
                    <div key={index} className="flex gap-2">
                      <Input value={item} onChange={(e) => handleArrayChange('organizationalAssessments', index, e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('organizationalAssessments', index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Detail Banner (Page Detail)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  {formData.image ? (
                    <div className="relative aspect-video rounded-lg border overflow-hidden group">
                      <MediaImg src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage('image')}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-video rounded-lg border-2 border-dashed border-light flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-light transition-all"
                    >
                      <Upload className="h-8 w-8 text-gray mb-2" />
                      <span className="text-xs text-gray font-medium">Click to upload image</span>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileChange(e, 'image')}
                    className="hidden"
                    accept="image/*"
                  />
                  
                  <div className="space-y-2">
                    <Label htmlFor="image">Image Path</Label>
                    <Input
                      id="image"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="/upload/services/..."
                      className="text-xs"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Home Banner (Homepage)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  {formData.homeImage ? (
                    <div className="relative aspect-video rounded-lg border overflow-hidden group">
                      <MediaImg src={formData.homeImage} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage('homeImage')}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => homeFileInputRef.current?.click()}
                      className="aspect-video rounded-lg border-2 border-dashed border-light flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-light transition-all"
                    >
                      <Upload className="h-8 w-8 text-gray mb-2" />
                      <span className="text-xs text-gray font-medium">Click to upload image</span>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={homeFileInputRef}
                    onChange={(e) => handleFileChange(e, 'homeImage')}
                    className="hidden"
                    accept="image/*"
                  />
                  
                  {isUploading && (
                    <p className="text-xs text-center text-gray animate-pulse">Uploading...</p>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="homeImage">Image Path</Label>
                    <Input
                      id="homeImage"
                      name="homeImage"
                      value={formData.homeImage}
                      onChange={handleChange}
                      placeholder="/upload/services/..."
                      className="text-xs"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Display Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="categoryId">Category</Label>
                  <Select value={formData.categoryId} onValueChange={handleSelectChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order">Display Order</Label>
                  <Input id="order" name="order" type="number" value={formData.order} onChange={handleChange} />
                </div>
              </CardContent>
            </Card>

            {/* List Sections */}
            <ArrayInputCard title="Key Features" field="features" data={formData.features} onAdd={() => addArrayItem('features')} onRemove={removeArrayItem} onChange={handleArrayChange} />
            <ArrayInputCard title="Expected Outputs" field="outputs" data={formData.outputs} onAdd={() => addArrayItem('outputs')} onRemove={removeArrayItem} onChange={handleArrayChange} />
            <ArrayInputCard title="Program Modules" field="modules" data={formData.modules} onAdd={() => addArrayItem('modules')} onRemove={removeArrayItem} onChange={handleArrayChange} />

            {/* Related Insights Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Related Insights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                  {allInsights.map((insight) => (
                    <label 
                      key={insight.id} 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${formData.insightIds.includes(insight.id) ? 'bg-primary/5 border-primary' : 'bg-white border-gray-100 hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={formData.insightIds.includes(insight.id)}
                        onChange={() => handleInsightToggle(insight.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-navy truncate">{insight.title}</p>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                          {insight.serviceCategory?.name || 'Uncategorized'}
                        </p>
                      </div>
                    </label>
                  ))}
                  {allInsights.length === 0 && (
                    <p className="text-xs text-center text-gray-400 py-4">No insights available to link.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-navy hover:bg-primary transition-all" disabled={isLoading}>
              {isLoading ? 'Saving...' : <><Save className="mr-2 h-4 w-4" /> Save Service</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function ArrayInputCard({ title, field, data, onAdd, onRemove, onChange }: any) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-sm">{title}</CardTitle>
        <Button type="button" variant="ghost" size="sm" onClick={onAdd}>
          <Plus className="h-3 w-3" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2 pb-4">
        {data.map((item: string, index: number) => (
          <div key={index} className="flex gap-2">
            <Input value={item} onChange={(e) => onChange(field, index, e.target.value)} className="text-xs" />
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onRemove(field, index)}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
